import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import Navbar from '../components/common/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getOnlineStatus } from '../services/api';
import { formatMoney, formatNumber, plural } from '../utils/formatters';
import '../components/common/ui.css';
import './OnlinePage.css';

const REFRESH_MS = 3 * 60 * 1000; // как в фоновом таймере на сервере — нет смысла опрашивать чаще, чем данные обновляются

const Icon = ({ children }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {children}
  </svg>
);
const IconTable = () => <Icon><path d="M2 8H22" /><path d="M6 8V21" /><path d="M18 8V21" /><path d="M4 8C4 5.17157 4 3.75736 4.87868 2.87868C5.75736 2 7.17157 2 10 2H14C16.8284 2 18.2426 2 19.1213 2.87868C20 3.75736 20 5.17157 20 8" /></Icon>;
const IconGuests = () => <Icon><circle cx="9" cy="6" r="4" /><path d="M15 9C16.6569 9 18 7.65685 18 6C18 4.34315 16.6569 3 15 3" /><ellipse cx="9" cy="17" rx="7" ry="4" /><path d="M18 14C19.7542 14.3847 21 15.3589 21 16.5C21 17.5293 19.9863 18.4229 18.5 18.8704" /></Icon>;
const IconWallet = () => <Icon><path d="M6 10H10" /><path d="M20.8333 11H18.2308C16.4465 11 15 12.3431 15 14C15 15.6569 16.4465 17 18.2308 17H20.8333C20.9167 17 20.9583 17 20.9935 16.9979C21.5328 16.965 21.9623 16.5662 21.9977 16.0654C22 16.0327 22 15.994 22 15.9167V12.0833C22 12.006 22 11.9673 21.9977 11.9346C21.9623 11.4338 21.5328 11.035 20.9935 11.0021C20.9583 11 20.9167 11 20.8333 11Z" /><path d="M20.965 11C20.8873 9.1277 20.6366 7.97975 19.8284 7.17157C18.6569 6 16.7712 6 13 6H10C6.22876 6 4.34315 6 3.17157 7.17157C2 8.34315 2 10.2288 2 14C2 17.7712 2 19.6569 3.17157 20.8284C4.34315 22 6.22876 22 10 22H13C16.7712 22 18.6569 22 19.8284 20.8284C20.6366 20.0203 20.8873 18.8723 20.965 17" /><path d="M6 6L9.73549 3.52313C10.7874 2.82562 12.2126 2.82562 13.2645 3.52313L17 6" /><path d="M17.9912 14H18.0002" /></Icon>;
const IconReceipt = () => <Icon><path d="M16.755 2H7.24502C6.08614 2 5.50671 2 5.03939 2.16261C4.15322 2.47096 3.45748 3.18719 3.15795 4.09946C3 4.58055 3 5.17705 3 6.37006V20.3742C3 21.2324 3.985 21.6878 4.6081 21.1176C4.97417 20.7826 5.52583 20.7826 5.8919 21.1176L6.375 21.5597C7.01659 22.1468 7.98341 22.1468 8.625 21.5597C9.26659 20.9726 10.2334 20.9726 10.875 21.5597C11.5166 22.1468 12.4834 22.1468 13.125 21.5597C13.7666 20.9726 14.7334 20.9726 15.375 21.5597C16.0166 22.1468 16.9834 22.1468 17.625 21.5597L18.1081 21.1176C18.4742 20.7826 19.0258 20.7826 19.3919 21.1176C20.015 21.6878 21 21.2324 21 20.3742V6.37006C21 5.17705 21 4.58055 20.842 4.09946C20.5425 3.18719 19.8468 2.47096 18.9606 2.16261C18.4933 2 17.9139 2 16.755 2Z" /><path d="M10.5 11L17 11" /><path d="M7 11H7.5" /><path d="M7 7.5H7.5" /><path d="M7 14.5H7.5" /><path d="M10.5 7.5H17" /><path d="M10.5 14.5H17" /></Icon>;
const IconInfo = () => <Icon><circle cx="12" cy="12" r="10" /><path d="M12 16V12" /><path d="M12 8H12.01" /></Icon>;
const IconEmpty = () => <Icon><path d="M2 8H22" /><path d="M6 8V21" /><path d="M18 8V21" /><path d="M4 8C4 5.17157 4 3.75736 4.87868 2.87868C5.75736 2 7.17157 2 10 2H14C16.8284 2 18.2426 2 19.1213 2.87868C20 3.75736 20 5.17157 20 8" /></Icon>;

const formatDuration = (minutes) => {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes} ${plural(minutes, 'минута', 'минуты', 'минут')}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h} ${plural(h, 'час', 'часа', 'часов')}${m ? ` ${m} мин` : ''}`;
};

const durationClass = (minutes) => {
  if (minutes === null || minutes === undefined) return '';
  if (minutes >= 150) return 'danger';
  if (minutes >= 90) return 'warning';
  return '';
};

const OnlinePage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const timerRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const result = await getOnlineStatus();
      setData(result);
      setError('');
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить данные зала');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(false);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  if (!isAuthenticated) return null;

  const tables = data?.tables || [];
  const summary = data?.summary || {};
  const updatedAtDate = data?.updated_at ? new Date(data.updated_at) : null;
  const updatedAt = updatedAtDate ? updatedAtDate.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : null;
  // iiko иногда подвисает на минуты — если фоновое обновление давно не
  // проходило, числа на экране всё ещё «живые» по написанию, но могут не
  // отражать реальность, и это стоит явно показать, а не молча тикать секунды.
  const staleMinutes = updatedAtDate ? Math.floor((Date.now() - updatedAtDate.getTime()) / 60000) : null;
  const isStale = staleMinutes !== null && staleMinutes >= 2;

  return (
    <div className="online-container">
      <Navbar user={user} isAdmin={user?.role === 'admin'} onLogout={logout} />

      <div className="online-header">
        {isStale && (
          <div className="online-stale-badge">
            Данные не обновлялись {staleMinutes} {plural(staleMinutes, 'минуту', 'минуты', 'минут')} — iiko отвечает медленно
          </div>
        )}
        <div className={`online-live-badge ${isStale ? 'stale' : ''}`}>
          <span className="online-pulse-dot" />
          <span>{updatedAt ? `Обновлено: ${updatedAt}` : 'Загрузка…'}</span>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : error ? (
        <div className="ui-inline-error online-error">
          <span>{error}</span>
          <button type="button" className="btn-primary" onClick={() => load(false)}>Повторить</button>
        </div>
      ) : data && data.supported === false ? (
        <div className="panel online-unsupported">
          <div className="online-unsupported-icon"><IconInfo /></div>
          <div>
            <h2>Раздел недоступен</h2>
            <p>{data.message}</p>
          </div>
        </div>
      ) : (
        <>
          <div className="online-stats">
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon purple"><IconTable /></div>
                <div className="stat-title">Открытых столов</div>
              </div>
              <div className="stat-value">{formatNumber(summary.open_count || 0)}</div>
              <div className="stat-label">сейчас в зале</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon green"><IconWallet /></div>
                <div className="stat-title">Сумма на столах</div>
              </div>
              <div className="stat-value">{formatMoney(summary.total_sum)}</div>
              <div className="stat-label">ещё не оплачено</div>
            </div>
            <div className="stat-card">
              <div className="stat-header">
                <div className="stat-icon blue"><IconGuests /></div>
                <div className="stat-title">Гостей в зале</div>
              </div>
              <div className="stat-value">{formatNumber(summary.total_guests || 0)}</div>
              <div className="stat-label">по открытым заказам</div>
            </div>
          </div>

          <div className="panel">
            <h2><IconTable /> Открытые столы</h2>

            {tables.length === 0 ? (
              <div className="online-empty">
                <IconEmpty />
                <p>Сейчас нет открытых столов</p>
              </div>
            ) : (
              <div className="online-grid">
                {tables.map((t) => (
                  <div key={t.order_id} className={`online-card ${durationClass(t.open_minutes)}`}>
                    <div className="online-card-top">
                      <span className="online-card-table">Стол {t.table}</span>
                      {t.precheque && <span className="badge badge-precheque"><IconReceipt /> Ждут счёт</span>}
                    </div>
                    <div className="online-card-sum">{formatMoney(t.sum)}</div>
                    <div className="online-card-meta">
                      <span><IconGuests /> {formatNumber(t.guests)}</span>
                      <span className="online-card-duration">{formatDuration(t.open_minutes)}</span>
                    </div>
                    <div className="online-card-waiter">{t.waiter}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default OnlinePage;
